import React, { useState, useEffect, useCallback } from "react";
import BankAccountService from "../../../services/bank_account_service";
import { useTenant } from "../../../hooks/useTenant";
import { formatCurrency } from "../../Financial/DRE/utils/dreCalculations";
import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS, FINANCIAL_TYPES } from "../../../constants/financial";
import { NeoModal, NeoButton, NeoInput, NeoDatePicker } from "../../../components/Common";

// Opções de pagamento derivadas da constante global
const PAYMENT_OPTIONS = Object.values(PAYMENT_METHODS).map(value => ({
    value,
    label: PAYMENT_METHOD_LABELS[value] || value
}));

/**
 * PaymentConfirmModal
 * Modal profissional de confirmação de baixa (recebimento ou pagamento).
 * Usado tanto em Contas a Receber quanto em Contas a Pagar.
 *
 * Props:
 * - isOpen: boolean
 * - toggle: fn
 * - record: o financialRecord sendo processado
 * - type: FINANCIAL_TYPES — define o título e labels
 * - onConfirm: fn(recordId, paymentData) — chamado ao confirmar
 * - processing: boolean
 */
const PaymentConfirmModal = ({ isOpen, toggle, record, type = FINANCIAL_TYPES.INCOME, onConfirm, processing }) => {
    const { idTenant } = useTenant();
    const isIncome = type === FINANCIAL_TYPES.INCOME;

    // State
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);

    const [formData, setFormData] = useState({
        paymentDate: new Date().toISOString().split("T")[0],
        paymentMethod: "",
        idBankAccount: "",
        bankAccountName: "",
        amountPaid: "",
        observation: "",
    });

    // Carrega contas bancárias ao abrir
    useEffect(() => {
        if (!isOpen || !idTenant) return;
        setLoadingAccounts(true);
        BankAccountService.getBankAccounts(idTenant)
            .then(accounts => setBankAccounts(accounts.filter(a => a.status !== "INACTIVE")))
            .catch(err => console.error("[PaymentConfirmModal] Erro ao carregar contas:", err))
            .finally(() => setLoadingAccounts(false));
    }, [isOpen, idTenant]);

    // Preenche valores ao receber o record
    useEffect(() => {
        if (!record) return;
        setFormData({
            paymentDate: new Date().toISOString().split("T")[0],
            paymentMethod: record.paymentMethod || "",
            idBankAccount: record.idBankAccount || "",
            bankAccountName: record.bankAccountName || "",
            amountPaid: record.netAmount || record.grossAmount || "",
            observation: "",
        });
    }, [record]);

    const handleChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleBankAccountChange = useCallback((accountId) => {
        const acc = bankAccounts.find(a => a.id === accountId);
        setFormData(prev => ({
            ...prev,
            idBankAccount: accountId,
            bankAccountName: acc?.description || "",
        }));
    }, [bankAccounts]);

    const handleSubmit = useCallback((e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!record || !formData.paymentDate || !formData.idBankAccount) return;
        onConfirm(record.id, {
            paymentDate: formData.paymentDate,
            paymentMethod: formData.paymentMethod || null,
            idBankAccount: formData.idBankAccount,
            bankAccountName: formData.bankAccountName,
            amountPaid: Number(formData.amountPaid) || record.netAmount || record.grossAmount || 0,
            observation: formData.observation || null,
        });
    }, [record, formData, onConfirm]);

    const isValid = formData.paymentDate && formData.idBankAccount;

    if (!record) return null;

    const modalFooterActions = (
        <>
            <NeoButton variant="light" className="text-muted px-4" onClick={toggle} disabled={processing}>
                Cancelar
            </NeoButton>
            <NeoButton
                variant={isIncome ? "success" : "primary"}
                onClick={handleSubmit}
                disabled={!isValid || processing}
                loading={processing}
                loadingText="Confirmando..."
                icon="mdi-check"
                className="px-4"
            >
                {isIncome ? "Confirmar Recebimento" : "Confirmar Pagamento"}
            </NeoButton>
        </>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={toggle}
            size="md"
            centered
            title={isIncome ? "Confirmar Recebimento" : "Confirmar Pagamento"}
            headerIcon={isIncome ? "mdi-cash-plus" : "mdi-cash-minus"}
            footerActions={modalFooterActions}
        >
            <form onSubmit={handleSubmit}>
                {/* Info do registro */}
                <div
                    className="p-3 mb-3 animate__animated animate__fadeIn"
                    style={{
                        background: "var(--color-bg-base)",
                        borderRadius: "12px",
                        border: "1.5px solid rgba(72, 39, 18, 0.12)",
                        fontFamily: "Outfit, sans-serif"
                    }}
                >
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <span className="fw-bold font-size-14" style={{ color: "var(--color-brand-primary)" }}>
                                {record.description || "Sem descrição"}
                            </span>
                            {record.memberName && (
                                <small className="d-block text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                                    {record.memberName}
                                </small>
                            )}
                            {record.entityName && !record.memberName && (
                                <small className="d-block text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                                    {record.entityName}
                                </small>
                            )}
                        </div>
                        <div className="text-end">
                            <span className={`fw-extrabold ${isIncome ? "text-success" : "text-danger"}`} style={{ fontSize: "1.15rem", fontFamily: "Outfit, sans-serif" }}>
                                {formatCurrency(record.netAmount || record.grossAmount)}
                            </span>
                            {record.feeAmount > 0 && (
                                <small className="d-block text-muted mt-1" style={{ fontSize: "0.72rem" }}>
                                    Bruto: {formatCurrency(record.grossAmount)} | Taxa: {formatCurrency(record.feeAmount)}
                                </small>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row g-3">
                    {/* Data do pagamento */}
                    <div className="col-md-6 animate__animated animate__fadeInUp">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Data do {isIncome ? "Recebimento" : "Pagamento"} *
                        </label>
                        <NeoDatePicker
                            value={formData.paymentDate}
                            onChange={(dateStr) => handleChange("paymentDate", dateStr)}
                            size="sm"
                            className="mb-0 w-100"
                        />
                    </div>

                    {/* Forma de pagamento */}
                    <div className="col-md-6 animate__animated animate__fadeInUp">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>Forma de Pagamento</label>
                        <NeoInput
                            type="select"
                            size="sm"
                            value={formData.paymentMethod}
                            onChange={(e) => handleChange("paymentMethod", e.target.value)}
                            className="mb-0"
                        >
                            <option value="">Selecione...</option>
                            {PAYMENT_OPTIONS.map(m => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </NeoInput>
                    </div>

                    {/* Conta Bancária */}
                    <div className="col-md-6 animate__animated animate__fadeInUp">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>Conta Bancária *</label>
                        {loadingAccounts ? (
                            <div className="d-flex align-items-center py-1 gap-2">
                                <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: "14px", height: "14px" }} />
                                <small className="text-muted" style={{ fontSize: "0.75rem", fontFamily: "Outfit, sans-serif" }}>Carregando contas...</small>
                            </div>
                        ) : (
                            <NeoInput
                                type="select"
                                size="sm"
                                value={formData.idBankAccount}
                                onChange={(e) => handleBankAccountChange(e.target.value)}
                                className="mb-0"
                            >
                                <option value="">Selecione a conta...</option>
                                {bankAccounts.map(a => (
                                    <option key={a.id} value={a.id}>{a.description}</option>
                                ))}
                            </NeoInput>
                        )}
                        {bankAccounts.length === 0 && !loadingAccounts && (
                            <small className="text-danger d-block mt-1 fw-semibold" style={{ fontSize: "0.72rem" }}>
                                Nenhuma conta cadastrada. Cadastre em Configurações → Contas Bancárias.
                            </small>
                        )}
                    </div>

                    {/* Valor efetivo */}
                    <div className="col-md-6 animate__animated animate__fadeInUp">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>
                            Valor {isIncome ? "Recebido" : "Pago"}
                        </label>
                        <NeoInput
                            type="number"
                            size="sm"
                            step="0.01"
                            min="0"
                            value={formData.amountPaid}
                            onChange={(e) => handleChange("amountPaid", e.target.value)}
                            placeholder="0,00"
                            className="mb-0"
                        />
                        <small className="text-muted d-block mt-1" style={{ fontSize: "0.7rem", fontFamily: "Outfit, sans-serif" }}>
                            Altere apenas se diferente do valor original.
                        </small>
                    </div>

                    {/* Observação */}
                    <div className="col-12 animate__animated animate__fadeInUp">
                        <label className="form-label small fw-bold text-muted text-uppercase mb-1" style={{ letterSpacing: "0.5px", fontFamily: "Outfit, sans-serif" }}>Observação</label>
                        <NeoInput
                            type="textarea"
                            size="sm"
                            rows={2}
                            value={formData.observation}
                            onChange={(e) => handleChange("observation", e.target.value)}
                            placeholder="Nota interna sobre este pagamento (opcional)"
                            className="mb-0"
                        />
                    </div>
                </div>
            </form>
        </NeoModal>
    );
};

export default PaymentConfirmModal;
